import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useForm, useWatch } from "react-hook-form";
import { z } from "zod";

import { Button } from "~/components";
import { useUsers } from "~/hooks";
import { personalSettingsSchema } from "~/screens/Auth/schemas/account.schema";
import { setFormErrors } from "~/shared/utils/setFormErrors";
import { Input } from "~/ui";
import { tw } from "~/utils";
import { SuccessMessage } from "./SuccessMessage";

type FormData = z.infer<typeof personalSettingsSchema>;

const LABEL_CLASS = "text-indigo-charcoal text-sm";

interface PersonalSettingsProps {
  className?: string;
}

export const PersonalSettings = ({ className }: PersonalSettingsProps) => {
  const {
    getCurrentUser,
    updateUserMutation: { mutateAsync: updateUser, isPending, isSuccess },
  } = useUsers();
  const { data: currentUser } = getCurrentUser();

  const queryClient = useQueryClient();

  const [isModified, setIsModified] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    control,
    setError,
  } = useForm<FormData>({
    resolver: zodResolver(personalSettingsSchema),
  });

  const firstName = useWatch({ control, name: "firstName" });
  const lastName = useWatch({ control, name: "lastName" });

  useEffect(() => {
    setIsModified(
      firstName !== currentUser?.firstName ||
        lastName !== currentUser?.lastName,
    );
  }, [firstName, lastName, currentUser]);

  useEffect(() => {
    if (currentUser && !isModified) {
      handleReset();
    }
  }, [currentUser]);

  const handleReset = () => {
    reset({
      firstName: currentUser?.firstName,
      lastName: currentUser?.lastName,
    });
  };

  const onSubmit = async (data: FormData) => {
    try {
      await updateUser(data);
      setIsModified(false);
      queryClient.invalidateQueries({ queryKey: ["getCurrentUser"] });
    } catch (e) {
      setFormErrors<FormData>(e, setError);
    }
  };

  return (
    <form
      className={tw("flex flex-col gap-8", className)}
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className="flex flex-col gap-6">
        <div className="grid grid-cols-2 gap-2">
          <Input
            id="firstName"
            required
            label="First name"
            labelClassName={LABEL_CLASS}
            placeholder="E.g. Jane"
            {...register("firstName")}
            error={errors.firstName?.message}
            className="w-full"
          />

          <Input
            id="lastName"
            required
            label="Last name"
            labelClassName={LABEL_CLASS}
            placeholder="E.g. Smith"
            {...register("lastName")}
            error={errors.lastName?.message}
            className="w-full"
          />
        </div>

        <Input
          id="email"
          disabled
          label="Email address"
          labelClassName={LABEL_CLASS}
          value={currentUser?.email}
          className="w-full"
        />
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex justify-evenly gap-4 md:justify-start">
          <Button
            size="default"
            type="button"
            variant="outline"
            className="w-fit px-8"
            onClick={handleReset}
          >
            Cancel
          </Button>

          <Button
            size="default"
            type="submit"
            disabled={isPending || !isModified}
            className="w-fit px-8"
          >
            Save changes
          </Button>
        </div>

        {isSuccess && !isPending && <SuccessMessage />}
      </div>
    </form>
  );
};
